import './style.scss';

import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import { getUserData, ISADMINLOGGED, ISOBSERVER, ISSUPPORTER } from '../../constants/global';
import { pageurl } from '../../constants/pageurl';
import { GlobalContext } from '../../context/global';
import { useAgentHierarchy } from '../../hooks/useAgentHierarchy';
import { useCancellationAutoFlush } from '../../hooks/useCancellationAutoFlush';
import { useChatUnread } from '../../hooks/useChatUnread';
import useLocationTracking from '../../hooks/useLocationTracking';
import { useObserverData } from '../../hooks/useObserverData';
import { usePeerInbox } from '../../hooks/usePeerInbox';
import { useResultVideoAutoFlush } from '../../hooks/useResultVideoAutoFlush';
import { useSupportChat } from '../../hooks/useSupportChat';
import { useSupportSession } from '../../hooks/useSupportSession';
import type { IActions } from '../../interface/IAction';
import type { IStates } from '../../interface/IReducer';
import { type IAgentChatPanelData } from '../../interface/peer-chat';
import { puCancellationEnabled } from '../../lib/cancellation-api';
import { initConsoleBuffer } from '../feedback/console-buffer';
import FeedbackFab from '../feedback/FeedbackFab';
import { useAppData, useGetCodes } from '../pages/upload-result/hooks';
import type { ursType } from '../pages/upload-result/utils';
import { TypeButton } from '../utils/button';
import { useCallAPI, useCopy, useInactivityTimeout, useSaveB4Changes } from '../utils/hooks';
import LocationTracker from '../utils/location-tracker';
import NotificationModal, { useModal } from '../utils/new-modal';
import { useRightSection } from '../utils/right-section';
import { ClockSVG } from '../utils/svgs';
import { useCountDown } from '../utils/timer';
import ChildWrapper from './child-wrapper';
import GlobalRightSection from './global-right-section';
import Header from './header';
import NotificationCard from './notification-card';
import PeerThreadHosts, { usePeerThreadRefs } from './PeerThreadHosts';
import SideMenu, { menuData } from './side-menu';

export interface IDashboard {
	authRoute?: boolean;
	children?: any;
	generalRoute?: boolean;
	publicRoute?: boolean;
	adminRoute?: boolean;
	props?: any;
}

const Dashboard = ({ ...rest }: IDashboard) => {
	const { adminRoute, children, authRoute, generalRoute, publicRoute, ...props } = rest;
	const { pathname } = useLocation();

	const rsProps = useRightSection();
	const copyProps = useCopy();
	// const navigate = useNavigate()

	const { states, ...propsactions } = props as unknown as {
		states: IStates;
		[x: string]: any;
	};

	const actions = propsactions as IActions;

	const { global, saveAsDraft, restoreDraft, isDraft, clearDraft, clearDraftLocal } = useAppData(actions, states);

	const useNotificationProps = useModal();

	// const isSessionTimeout = states?.global?.sessionTimeoutGlobal?.status

	const handleWarning = () => {
		// if user is logged in
		if (adminRoute) {
			useNotificationProps.handleOpenModal('Inactivity Alert');
		}
	};
	const handleSignOut = () => {
		actions?.logout_Auth({});
	};
	const stayLoggedIn = () => {
		useNotificationProps.handleCloseModal();
	};

	const isInactive = useInactivityTimeout(540000, handleWarning, states); // 1 minutes

	// The agent's assignment location code (e.g. "04-01-01-001") rides along with every
	// GPS ping. Tracking runs app-wide from the layout, but only the overview page fetches
	// the assignment — so fetch it here too when missing, so the code is available on any
	// admin route.
	const electionOfficial = states?._election?.get_ElectionOfficial?.data?.find(
		(i) => i.userId === Number(getUserData().user?.UserId)
	);
	const entityCode = electionOfficial?.assignment?.code;

	// Independent observers are never in ElectionOfficial and own none of the party machinery below
	// (location pings, cancellation and result-video queues, the agent hierarchy, the peer inbox).
	// Their postings come from useObserverData where they are read.
	const isObserver = ISOBSERVER();
	// Independent Supporters own none of it either, nor any of the observer machinery: no location
	// pings, no support or peer chat (api.vesr's hub refuses them, so the socket must never open).
	// Their home reads api.core's /Supporter routes and VESR incidents over plain HTTP.
	const isSupporter = ISSUPPORTER();
	const partyAgentSession = !!adminRoute && !isObserver && !isSupporter;

	useCallAPI(() => {
		actions.get_ElectionOfficial({
			query: [{ key: 'UserId', value: getUserData().user?.UserId }],
		});
	}, partyAgentSession && !electionOfficial);

	// Cancellation reports saved with no signal send themselves on app start and on `online` —
	// here, not in the panel, because panels unmount and a report must not wait for one.
	useCancellationAutoFlush(partyAgentSession && puCancellationEnabled());
	// Result videos upload in the background for the same reason: an agent closes the panel the
	// moment the result is in, and a three-minute video on 3G outlives any panel.
	useResultVideoAutoFlush(partyAgentSession);

	// Mounted HERE, beside useLocationTracking, not inside the chat panel. Right-section
	// panels unmount when inactive, so a panel-owned hook would tear down the moment an
	// agent closes the chat to go upload a result sheet -- taking an in-flight send with it
	// and leaving the FAB badge with nothing to count.
	const supportSession = useSupportSession(!isSupporter);
	const supportChat = useSupportChat(!!rsProps?.isView('view', 'support-chat'), supportSession);

	// Agent ↔ agent conversations ride the same session. The engines are hosted here for the
	// same reason the support one is; the panels only read what they publish.
	//
	// An independent observer rides the same engine with their own links: their organisation's
	// state observer supervises them (api.core me/observer-hierarchy), and VESR keys the thread by
	// organisation, never party. Their election and unit come from their posting.
	const observerData = useObserverData(isObserver && !!adminRoute);
	const observerPosting = isObserver ? observerData.postings[0] : undefined;

	// Observers are tracked like party agents: their desk needs to know they are on the ground and
	// safe. A ping carries whichever unit code the session is posted to.
	const locationTracking = useLocationTracking(
		!!adminRoute && !isSupporter,
		isObserver ? observerPosting?.entityCode : entityCode
	);
	// The observer home prints the same status on the pass itself, so the floating pill would repeat it.
	const onObserverHome = isObserver && pathname === pageurl.OVERVIEW;
	const electionId = partyAgentSession ? electionOfficial?.electionId : observerPosting?.electionId;
	const ownResultType = (
		partyAgentSession ? electionOfficial?.assignment?.resultType : observerPosting?.resultType
	)?.toLowerCase();
	const isParentAgent = isObserver ? ownResultType === 'ec8d' : !!ownResultType && ownResultType !== 'ec8a';
	const peerChatPanel = isObserver ? 'observer-chat' : 'agent-chat';
	const activeChat = rsProps?.isView('view', peerChatPanel) ? (rsProps.data as IAgentChatPanelData | null) : null;
	const activePeerLocalId =
		activeChat?.counterpart?.coreUserId && activeChat.electionId
			? `peer:${activeChat.electionId}:${activeChat.counterpart.coreUserId}`
			: null;
	const peerThreads = usePeerThreadRefs({ activeLocalId: activePeerLocalId, me: supportSession.me });
	const peerInbox = usePeerInbox(electionId, {
		session: supportSession,
		isInboxOpen: !!rsProps?.isView('view', isObserver ? 'observer-peers' : 'messages-inbox'),
	});
	const chatUnread = useChatUnread(supportChat.unread, peerInbox.threads, supportSession.me, isParentAgent);
	const agentHierarchy = useAgentHierarchy(
		electionId,
		isObserver ? observerPosting?.entityCode : entityCode,
		partyAgentSession || (!!adminRoute && !!observerPosting),
		isObserver ? 'observer' : 'agent'
	);

	/**
	 * `?support=1` opens the conversation directly.
	 *
	 * <p>The offline WhatsApp fallback tells an agent the desk is waiting and then drops them
	 * into the app with no idea where to go, which throws away half the value of having
	 * chased them. This is the link that message can point at.</p>
	 *
	 * <p>Runs once: the panel is a right-section component rather than a route, so there is no
	 * router change and nothing to clean up.</p>
	 */
	const supportDeepLinkHandled = useRef(false);
	useEffect(() => {
		if (supportDeepLinkHandled.current || !rsProps || isSupporter) return;
		if (typeof window === 'undefined') return;

		const params = new URLSearchParams(window.location.search);
		if (params.get('support') === '1') {
			supportDeepLinkHandled.current = true;
			rsProps.callSection({ action: 'view', component: 'support-chat', title: 'Support desk' });
		}
	}, [rsProps]);

	/**
	 * `?chat={counterpartCoreUserId}` opens a peer conversation — what a future nudge for a
	 * parent's message can point at. Waits for the hierarchy so the counterpart has a name and
	 * a unit; a stranger's id simply opens nothing.
	 */
	const chatDeepLinkHandled = useRef(false);
	useEffect(() => {
		if (chatDeepLinkHandled.current || !rsProps || typeof window === 'undefined') return;
		if (!electionId || agentHierarchy.status !== 'ready') return;
		const raw = new URLSearchParams(window.location.search).get('chat');
		const id = raw ? Number(raw) : 0;
		if (!id) return;
		chatDeepLinkHandled.current = true;
		const node = [...agentHierarchy.children, ...agentHierarchy.parents].find((n) => n.userId === id);
		if (!node) return;
		const role = agentHierarchy.parents.some((n) => n.userId === id) ? 'parent' : 'child';
		rsProps.callSection({
			action: 'view',
			component: peerChatPanel,
			title: node.fullName || (isObserver ? 'Observer' : 'Agent'),
			data: {
				counterpart: {
					coreUserId: id,
					name: node.fullName || 'Agent',
					entityCode: node.entityCode,
					level: node.level,
					role,
					phone: node.phoneNumber ?? undefined,
				},
				electionId,
				origin: 'deep-link',
			} satisfies IAgentChatPanelData,
		});
	}, [rsProps, electionId, agentHierarchy.status, agentHierarchy.children, agentHierarchy.parents, peerChatPanel, isObserver]);

	useSaveB4Changes();

	const onStage = (stage: ursType) => {
		saveAsDraft();
		global.updateState('stage', stage);
	};

	const getCodeProps = useGetCodes({ actions, states, global });

	useEffect(() => {
		initConsoleBuffer();
	}, []);

	useEffect(() => {
		if (ISADMINLOGGED() && global.state.selectedParentCode?.code) {
			getCodeProps.handleCodes();
		}
	}, [global.state.parentCodes.length]);

	return (
		<>
			<NotificationModal
				useNotificationProps={useNotificationProps}
				size='wide'
				disableClose
			>
				<InactivityAlert
					stayLoggedIn={stayLoggedIn}
					handleSignOut={handleSignOut}
					startTimer={isInactive}
				/>
			</NotificationModal>

			<GlobalContext.Provider
				value={{
					rsProps,
					copyProps,
					states,
					actions: actions,
					global,
					saveAsDraft,
					restoreDraft,
					clearDraft,
					clearDraftLocal,
					isDraft,
					onStage,
					getCodeProps,
					locationTracking,
					supportChat,
					supportSession,
					peerThreads,
					peerInbox,
					chatUnread,
					agentHierarchy,
				}}
			>
				{adminRoute && !isSupporter ? (
					<PeerThreadHosts
						refs={peerThreads.openRefs}
						session={supportSession}
						activeLocalId={activePeerLocalId}
					/>
				) : null}
				<NotificationCard
					actions={actions}
					notice={states?.global?.notificationGlobal?.notice || ''}
					status={states?.global?.notificationGlobal?.status || false}
				/>
				{adminRoute && locationTracking.status !== 'idle' && !onObserverHome && (
					<LocationTracker
						variant='fixed'
						className='after-lg'
						tracking={locationTracking}
					/>
				)}
				<GlobalRightSection />
				{/* The FAB leads to the support and peer chats, which a supporter cannot use. */}
				{adminRoute && !isSupporter ? <FeedbackFab /> : null}
				<div className='main-container'>
					<div className='child-container'>
						{adminRoute ? (
							<SideMenu
								menu={menuData}
								actions={actions}
							/>
						) : null}
						<div className='main-content-container w-100'>
							{adminRoute ? (
								<Header
									actions={actions}
									rsProps={rsProps}
								/>
							) : null}
							<ChildWrapper>{children}</ChildWrapper>
						</div>
					</div>
				</div>
			</GlobalContext.Provider>
		</>
	);
};

export default Dashboard;

const InactivityAlert = ({
	stayLoggedIn,
	handleSignOut,
	startTimer,
}: {
	stayLoggedIn: () => void;
	handleSignOut: () => void;
	startTimer: boolean;
}) => {
	const { formatted: countdown, seconds: countdownSeconds } = useCountDown(20, startTimer);
	const isExpired = countdownSeconds === 0;

	useEffect(() => {
		if (isExpired) {
			handleSignOut();
		}
	}, [isExpired]);

	return (
		<div className='f-column-33 text-center p-4 align-items-center'>
			<div className='f-row-10 align-items-center justify-content-center'>
				<ClockSVG />
				<h4 className='m-0 color-danger'>Inactivity Alert</h4>
			</div>
			<p className='text-little'>
				We noticed that you have been inactive for a while. To keep your account secure, you will be automatically logged out in{' '}
				<span className='color-danger'>{countdown}</span> if no activity is detected.
			</p>
			<TypeButton
				title='Please Click here to stay logged in'
				onClick={stayLoggedIn}
				buttonSize='small'
			/>
		</div>
	);
};
